import { Button } from "react-bootstrap";
import React, { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { timeFrames } from "./liteTradeChartConstants.js";
import { createStructuredSelector } from "reselect";
import { getUserSelectedAccountCode } from "../../../../app/appSelectors.js";
import { getLiteTradeConfig, getLiteTradeSession } from "../../liteTradeSelectors.js";
import { connect } from "react-redux";
import useWebSocket from "react-use-websocket";
import moment from "moment/moment.js";
import _ from "lodash";
import { postLiteTradeRequest } from "../../liteTradeRequest.js";
import LiteTradeChartCandle from "./LiteTradeChartCandle.jsx";
import LiteTradeChartLine from "./LiteTradeChartLine.jsx";
import { BinIcon } from "../../../../components/icon/Icon.jsx";
import fillMissedCandles from "../../../../utils/fillMissedCandles.js";
import { Loader } from "../../../../components/Loader/Loader.jsx";
import { scaleTime } from "d3-scale";
import {
    LiteTradeChartWrapperStyled,
    LiteTradeChartWrapperTimeframesStyled
} from "./styled/LiteTradeChartWrapperStyled.js";


function LiteTradeChartWrapper(props) {
    const {
        decimal,
        lite,
        symbol,

        liteTradeConfig,
        session,
        selectedAccountCode,
    } = props;

    const [countCandles, setCountCandles] = useState(100);
    const [waitHistoricalData, setWaitHistoricalData] = useState(null);
    const [typeChart, setTypeChart] = useState(lite ? 'line' : (localStorage.getItem('selectedTypeChart') || 'line'));
    const {sendMessage, lastMessage} = useWebSocket(liteTradeConfig.wssMdUrl, {
        shouldReconnect: (e) => true,
    });
    const [chartLoader, setChartLoader] = useState(false);
    const [tf, setTf] = useState(lite ? 'm' : (localStorage.getItem('selectedTf') || '15m'));
    const lastSubscription = useRef();
    const [data, setData] = useState([]);
    const [xExtents, setXExtents] = useState([]);

    const xAccessor = useMemo(() => (d) => d?.date, []);
    const xScale = useMemo(scaleTime, []);
    const yAccessor = useMemo(() => (d) => d?.close, []);
    const yExtentsCalculator = useCallback(({plotData}) => {
        if (!plotData) {
            return [];
        }
        let min, max;
        for (const {low, high} of plotData) {
            if (min === undefined && !isNaN(low)) min = low;
            if (max === undefined && !isNaN(high)) max = high;
            if (low !== undefined && min > low) min = low;
            if (high !== undefined && max < high) max = high;
        }
        if (min === undefined) min = 0;
        if (max === undefined) max = 0;

        const padding = (max - min) * 0.1;
        return [min - padding, max + padding];
    }, []);

    const chartMargin = useMemo(() => {
        const price = data[data.length - 1]?.close;
        return {
            top: 0,
            right: ((price?.toFixed?.(0).length || 0) + decimal + 1) * 8 + 4,
            bottom: lite ? 0 : 40,
            left: 0
        };
    }, [data, decimal, lite]);

    const lastPositive = useMemo(() => {
        const last = _.last(data);
        return last?.open < last?.close;
    }, [data]);

    const tfClickHandler = useCallback((e) => {
        e.stopPropagation();
        const newTf = e.target.dataset.tf;
        if (newTf !== tf && timeFrames[newTf]) {
            // setData([]);
            localStorage.setItem('selectedTf', newTf);
            setTf(newTf)
        }
    }, [tf]);

    const setMarketDataEventsHandler = useCallback((data, historical = false) => {
        if (data?.[0]?.symbol !== symbol || data[0].candleType !== tf) {
            return;
        }
        const formatted = data
            .filter(item => !isNaN(item.close))
            .map(item => ({
                date: new Date(item.time),
                ...item,
            }));
        if (!formatted.length) return;
        if (formatted.length > 1 && formatted[0].date > formatted[formatted.length - 1].date) formatted.reverse();

        setData(prevState => {
            if (!prevState.length || (prevState[0]?.candleType !== formatted[0]?.candleType)) {
                const max = moment(formatted[formatted.length - 1].time).add(3 * timeFrames[tf].inMin, 'minutes').toDate();
                const min = moment(max).add(-35 * timeFrames[tf].inMin, 'minutes').toDate();
                setXExtents([min, max]);

                return fillMissedCandles(_.sortBy(formatted, 'time'), timeFrames[tf].inMin);
            } else {
                if (formatted.length === 1 && formatted[0].close === prevState[prevState.length - 1].close) {
                    return prevState;
                }
                let added = false;
                const mergedData = [...prevState];
                formatted.forEach(item => {
                    const idx = _.findIndex(mergedData, {date: item.date});
                    if (idx >= 0) {
                        mergedData[idx].close = item.close;
                        mergedData[idx].open = item.open;
                        mergedData[idx].high = item.high;
                        mergedData[idx].low = item.low;
                    } else {
                        historical ? mergedData.unshift(item) : mergedData.push(item);
                        added = true;
                    }
                });
                if (formatted.length === 1) {
                    const prevTime = moment(formatted[0].time).add(-timeFrames[tf].inMin, 'minutes').toDate()
                    const idx = _.findIndex(mergedData, {date: prevTime});
                    if (idx === -1) {
                        // Данные пропущены
                        setWaitHistoricalData(prevState => {
                            return prevState || prevTime;
                        });
                    }
                    return mergedData;
                } else if (formatted.length > 1) {
                    return fillMissedCandles(_.sortBy(mergedData, 'time'), timeFrames[tf].inMin);
                } else {
                    return mergedData;
                }
            }
        });
    }, [symbol, tf]);

    const getFutureData = useCallback((from, to) => {
        setData(prevState => {
            return prevState.slice(-countCandles);
        });
    }, [countCandles]);

    const getHistoricalData = useCallback((from, to = from, count = 0) => {
        // console.log('cl getHistoricalData', from, to, count);
        const fromTime = moment(from).add(-timeFrames[tf].inMin * countCandles, 'minutes').startOf(timeFrames[tf].startOf).toISOString()
        const toTime = moment(to).startOf(timeFrames[tf].startOf).toISOString();
        // console.log('cl fromTime, toTime', fromTime, toTime);
        const params = {
            account: `default:${selectedAccountCode}`,
            symbols: [symbol],
            eventTypes: [
                {
                    type: "Candle",
                    format: "COMPACT",
                    candleType: tf,
                    fromTime,
                    toTime,
                }
            ],
        };
        postLiteTradeRequest('/marketdata', params)
            .then(res => {
                if (res.events.length) {
                    setMarketDataEventsHandler(res.events, true);
                    setChartLoader(false);
                }
                if ((res.events.length === 0 && count < 10) || (res.events.length < countCandles && count === 0)) {
                    getHistoricalData(fromTime, fromTime, count + 1);
                }
            })
            .catch(e => {
                console.error('cl e', e);
            });

    }, [selectedAccountCode, symbol, tf, countCandles, setMarketDataEventsHandler]);

    const getHistoricalDataDebounce = useMemo(() => _.debounce(getHistoricalData, 500), [getHistoricalData]);

    const typeChartClickHandler = useCallback(() => {
        setTypeChart(prevState => {
            const newState = prevState === 'line' ? 'candle' : 'line'
            localStorage.setItem('selectedTypeChart', newState);
            return newState;
        });
    }, []);

    // Если данные были пропущены, запросить историю
    useEffect(() => {
        if (waitHistoricalData) {
            getHistoricalData(waitHistoricalData);
            setWaitHistoricalData(null);
        }
    }, [getHistoricalData, waitHistoricalData]);

    useEffect(() => {
        if (!selectedAccountCode) {
            return;
        }
        if (!timeFrames[tf]) {
            setTf('15m');
            return;
        }
        setChartLoader(true);
        if (lastSubscription.current) {
            sendMessage(JSON.stringify({
                type: "MarketDataCloseSubscriptionRequest",
                refRequestId: lastSubscription.current.requestId,
                session: session.sessionToken,
            }));
        }
        const requestId = `MarketDataSubscriptionRequest-${selectedAccountCode}`;
        // const addMinutes = lite ? 60 : timeFrames[tf].inMin * 50; // 50 свечек
        const subscriptionParams = {
            type: "MarketDataSubscriptionRequest",
            requestId,
            payload: {
                account: `default:${selectedAccountCode}`,
                symbols: [symbol],
                eventTypes: [
                    {
                        type: "Candle",
                        format: "COMPACT",
                        candleType: tf,
                        fromTime: moment().startOf(timeFrames[tf].startOf).toISOString(),
                    }
                ]
            },
            session: session.sessionToken,
        };
        sendMessage(JSON.stringify(subscriptionParams));
        lastSubscription.current = subscriptionParams;

        const to = moment().startOf(timeFrames[tf].startOf).toISOString();
        getHistoricalData(to, to);
    }, [lite, tf, symbol, sendMessage, selectedAccountCode, session.sessionToken, getHistoricalData, countCandles]);

    // New messages handler
    useEffect(() => {
        if (lastMessage !== null) {
            // console.log('cl lastMessage');
            const lastMessageData = JSON.parse(lastMessage.data);
            switch (lastMessageData.type) {
                case 'MarketData':
                    setMarketDataEventsHandler(lastMessageData.payload.events);
                    break;
                default:
                    console.log('cl [SOCKET] Unknown message', lastMessageData);
            }
        }
    }, [lastMessage, sendMessage, session.sessionToken, setMarketDataEventsHandler]);

    const ChartComponent = useMemo(() => {
        return typeChart === 'line' ? LiteTradeChartLine : LiteTradeChartCandle;
    }, [typeChart]);

    return <LiteTradeChartWrapperStyled>
        {
            !!lite && !data.length && <Loader show={true}/>
        }
        {!lite && <Button variant='info'
                          size='sm'
                          style={{
                              whiteSpace: 'nowrap',
                              margin: '0 16px',
                              borderRadius: '12px',
                              boxShadow: 'none',
                              padding: '12px',
                              background: '#1B1C2D',
                              border: 'none',
                              position: 'absolute',
                              zIndex: 20
                          }}
                          onClick={typeChartClickHandler}
        ><BinIcon name={typeChart === 'line' ? 'IcChartCandle' : 'IcChartLine'}/></Button>
        }

        <ChartComponent data={data}
                        getHistoricalData={getHistoricalDataDebounce}
                        getFutureData={getFutureData}
                        xAccessor={xAccessor}
                        xExtents={xExtents}
                        xScale={xScale}
                        yAccessor={yAccessor}
                        yExtentsCalculator={yExtentsCalculator}
                        decimal={decimal}
                        lite={lite}
                        margin={chartMargin}
                        lastPositive={lastPositive}
        />

        {
            !lite && <LiteTradeChartWrapperTimeframesStyled className={'mb-3 flex-row gap-2'}>
                {Object.keys(timeFrames).map(item => <Fragment key={item}>
                    <Button variant='info'
                            size='sm'
                            disabled={tf === item}
                            data-tf={item}
                            onClick={tfClickHandler}
                    >{timeFrames[item].title}</Button>
                </Fragment>)}
            </LiteTradeChartWrapperTimeframesStyled>
        }

    </LiteTradeChartWrapperStyled>
}

const mapStateToProps = createStructuredSelector({
    selectedAccountCode: getUserSelectedAccountCode,
    session: getLiteTradeSession,
    liteTradeConfig: getLiteTradeConfig,
});

const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(memo(LiteTradeChartWrapper));
